import { Select } from "antd";
import { FormikErrors, FormikProps, FormikTouched } from "formik";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  IRuleCondition,
  IWorkflowRule,
  IWorkflowsSelectOption,
} from "../../../../../../../@types/Worflows";
import { ArrowDown } from "../../../../../../../assets/icons/ArrowDown";
import { IOption } from "../../../../../../../components/SelectDropdown";
import { TextField } from "../../../../../../../components/TextField";
import { getWorkflowOptionLabel } from "../../../../../../../utils/workflowOptionLabels";
import { ErrorMessage, selectCss } from "../../../WorkflowsBuilder.styles";
import {
  SelectContainer,
  TextFieldCss,
  popupOptionsCss,
} from "../RuleComponent.styles";

interface ISpirometryStatusFieldsProps {
  formik: FormikProps<IWorkflowRule>;
  spirometryStatuses?: IWorkflowsSelectOption[];
  occurrencePeriods?: IWorkflowsSelectOption[];
}

/**
 * EN-5089: "blow quality is X at least N times in a day/week/month". The period is a rolling
 * window counted back from the latest blow, not a calendar week or month.
 */
export function SpirometryStatusFields({
  formik,
  spirometryStatuses,
  occurrencePeriods,
}: ISpirometryStatusFieldsProps) {
  const { t } = useTranslation();

  const shouldShowConditionError = (field: keyof IRuleCondition): string => {
    const touchedArray = formik.touched.conditions as
      | FormikTouched<IRuleCondition>[]
      | undefined;
    const errorArray = formik.errors.conditions as
      | FormikErrors<IRuleCondition>[]
      | undefined;

    if (touchedArray && errorArray) {
      const touched = touchedArray[0];
      const error = errorArray[0];

      return touched?.[field] && error?.[field] ? (error[field] as string) : "";
    }

    return "";
  };

  const statusOptions: IOption[] = useMemo(
    () =>
      spirometryStatuses?.map((status) => ({
        label: getWorkflowOptionLabel("spirometryStatus", status, t),
        value: status.type,
        key: status.type,
      })) ?? [],
    [spirometryStatuses, t]
  );

  const periodOptions: IOption[] = useMemo(
    () =>
      occurrencePeriods?.map((period) => ({
        label: getWorkflowOptionLabel("occurrencePeriod", period, t),
        value: period.type,
        key: period.type,
      })) ?? [],
    [occurrencePeriods, t]
  );

  return (
    <>
      <SelectContainer
        isError={shouldShowConditionError("spirometryStatus") !== ""}
      >
        <p>{t("workflows_rule_status")}</p>
        <Select
          placeholder={t("workflows_rule_status")}
          value={formik.values.conditions[0].spirometryStatus}
          suffixIcon={<ArrowDown />}
          options={statusOptions}
          popupClassName={popupOptionsCss()}
          notFoundContent={t("workflows_rule_noData")}
          className={`${selectCss()} nopan nodrag`}
          onChange={(value) => {
            formik.setFieldValue("conditions[0].spirometryStatus", value);
          }}
        />
        <ErrorMessage>
          {shouldShowConditionError("spirometryStatus")}
        </ErrorMessage>
      </SelectContainer>

      <SelectContainer
        isError={shouldShowConditionError("occurrenceCount") !== ""}
      >
        <p>{t("workflows_rule_numberOfTimes")}</p>
        <TextField
          name="conditions[0].occurrenceCount"
          placeholder="1"
          value={formik.values.conditions[0].occurrenceCount}
          label=""
          onChange={formik.handleChange}
          backgroudColor="white"
          className={`${TextFieldCss()} nopan nodrag`}
          type="number"
        />
        <ErrorMessage>
          {shouldShowConditionError("occurrenceCount")}
        </ErrorMessage>
      </SelectContainer>

      <SelectContainer
        isError={shouldShowConditionError("occurrencePeriod") !== ""}
      >
        <p>{t("workflows_rule_inA")}</p>
        <Select
          placeholder={t("workflows_rule_period")}
          value={formik.values.conditions[0].occurrencePeriod}
          suffixIcon={<ArrowDown />}
          options={periodOptions}
          popupClassName={popupOptionsCss()}
          notFoundContent={t("workflows_rule_noData")}
          className={`${selectCss()} nopan nodrag`}
          onChange={(value) => {
            formik.setFieldValue("conditions[0].occurrencePeriod", value);
          }}
        />
        <ErrorMessage>
          {shouldShowConditionError("occurrencePeriod")}
        </ErrorMessage>
      </SelectContainer>
    </>
  );
}
